import {
  DEFAULT_INSTRUMENT_SLOT_COUNT,
  MAX_INSTRUMENT_SLOT_COUNT,
  clampInstrumentSlotCount,
  type ClipIR,
} from "../../../../src/lib/audio";

/** All tracks audible / none soloed. */
export function createDefaultMuteSolo(
  count: number = DEFAULT_INSTRUMENT_SLOT_COUNT,
): boolean[] {
  const n = clampInstrumentSlotCount(count);
  return Array.from({ length: n }, () => false);
}

/** Coerce stored / imported mute or solo arrays to length `count` booleans. */
export function normalizeMuteSoloFlags(
  raw: unknown,
  count: number = DEFAULT_INSTRUMENT_SLOT_COUNT,
): boolean[] {
  const n = clampInstrumentSlotCount(count);
  const list = Array.isArray(raw) ? raw : [];
  return Array.from({ length: n }, (_, i) => list[i] === true);
}

export function cloneMuteSoloFlags(flags: boolean[]): boolean[] {
  return normalizeMuteSoloFlags(flags, flags.length || DEFAULT_INSTRUMENT_SLOT_COUNT);
}

/** Read per-pattern mute flags for a pattern (length = track count). */
export function patternMuteFlags(
  clip: ClipIR,
  patternId: string | undefined,
  count: number = DEFAULT_INSTRUMENT_SLOT_COUNT,
): boolean[] {
  const n = clampInstrumentSlotCount(count);
  const pattern = clip.patterns.find(p => p.id === patternId) ?? clip.patterns[0];
  return Array.from({ length: n }, (_, i) => pattern?.tracks[i]?.muted === true);
}

/**
 * One-time migration: copy legacy project-global `muted[]` onto every pattern
 * track when no pattern already stores per-track mute.
 */
export function migrateLegacyMuteToPatterns(clip: ClipIR, muted: boolean[] | undefined): boolean {
  if (!muted?.some(Boolean)) {
    return false;
  }
  const already = clip.patterns.some(pattern =>
    pattern.tracks.some(track => track.muted === true),
  );
  if (already) {
    return false;
  }
  for (const pattern of clip.patterns) {
    for (let i = 0; i < pattern.tracks.length; i++) {
      if (muted[i] === true) {
        pattern.tracks[i]!.muted = true;
      }
    }
  }
  return true;
}

/**
 * Infer active track count from parallel project arrays / clip tracks.
 * Instruments / presets win when present (avoids muted[] leftovers re-padding
 * after a track delete). Otherwise max of other arrays / pattern width.
 * Missing everything → default 8. Clamped to 1..MAX_INSTRUMENT_SLOT_COUNT.
 */
export function inferTrackCount(parts: {
  instruments?: unknown;
  presets?: unknown;
  muted?: unknown;
  solo?: unknown;
  slotMix?: unknown;
  slotFx?: unknown;
  patternTrackCount?: number;
}): number {
  const instrumentCount = Array.isArray(parts.instruments) ? parts.instruments.length : 0;
  const presetCount = Array.isArray(parts.presets) ? parts.presets.length : 0;
  if (instrumentCount > 0) {
    return clampInstrumentSlotCount(instrumentCount);
  }
  if (presetCount > 0) {
    return clampInstrumentSlotCount(presetCount);
  }
  const lengths = [
    Array.isArray(parts.muted) ? parts.muted.length : 0,
    Array.isArray(parts.solo) ? parts.solo.length : 0,
    Array.isArray(parts.slotMix) ? parts.slotMix.length : 0,
    Array.isArray(parts.slotFx) ? parts.slotFx.length : 0,
    parts.patternTrackCount ?? 0,
  ];
  const max = Math.max(0, ...lengths);
  if (max <= 0) {
    return DEFAULT_INSTRUMENT_SLOT_COUNT;
  }
  return clampInstrumentSlotCount(Math.min(MAX_INSTRUMENT_SLOT_COUNT, max));
}
